import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { unwrapResult } from '@reduxjs/toolkit';
import dayjs, { Dayjs } from 'dayjs';
import { SelectChangeEvent } from '@mui/material';

import { dateNormalizer, errorNormalizer } from '../../../shared/Helpers/functions';
import { CustomAlert, CustomButton, CustomSelect, Picker, SearchSelect } from '../../../shared/uiComponents';
import { SelectDataProperties } from '../../../shared/uiComponents/Dropdown/searchSelect';
import { useCompareDates } from '../../../shared/Helpers/hooks';
import { Client, SessionType } from '../../../shared/Assets/sessionDetailsSVGs';
import { AdminTypes } from '../../../components/Menu';
import AddContentAdmin from './addSessionAdmin';
import { TimeInputsWrapper } from '../../Reports/helpers';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { createEvent } from '../../../redux/State/clientSlice/calendarSlice';
import { CreateEventProperties, SessionTypeProperties } from '../../../redux/API/ClientAPIHelpers/calendarProperties';
import { getUserClients } from '../../../redux/State/clientSlice/userClientSlice';
import {
	setAddScheduledSession,
	setClient,
	setSearchClient,
	setTherapist,
} from '../../../redux/State/clientSlice/calendarHelperSlice';
import { getSessionTypes } from '../../../redux/State/clientSlice/sessionSlice';
import {
	clearDiscretionarySessionState,
	getUserRemainingHours,
} from '../../../redux/State/userSlice/userSessionAllocationSlice';

const todayDayjs = dayjs();

const AddContent = ({ onClose }: { onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();

	const [pageSize, setPageSize] = useState<number>(10);
	const [sessionTypeId, setSessionTypeId] = useState<number>(0);
	const [clientList, setClientList] = useState<SelectDataProperties[]>([]);
	const [dateValue, setDateValue] = useState<Dayjs | null>(todayDayjs);
	const [startTimeValue, setStartTimeValue] = useState<Dayjs | null>(null);
	const [endTimeValue, setEndTimeValue] = useState<Dayjs | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [sessionTypes, setSessionTypes] = useState<SessionTypeProperties[]>([]);
	const userSessionAllocation = useSelector((state) => state.userSessionAllocation.userRemainingHours);

	const sessionTypesData = useSelector((state) => state.session.sessionTypes);
	const userId = useSelector((state) => state.account.user.id);
	const role = useSelector((state) => state.account.role);
	const userClients = useSelector((state) => state.userClient.userClients);
	const days = useSelector((state) => state.calendarHelper.displayedCalendarPageDays);
	const selectedDay = useSelector((state) => state.calendarHelper.selectedDay);
	const searchString = useSelector((state) => state.calendarHelper.filterData.searchClient);
	const clientId = useSelector((state) => state.calendarHelper.filterData.client?.id);

	const isBT = role?.section.id === AdminTypes.bt;

	const { hasError, message } = useCompareDates(startTimeValue, endTimeValue);

	useEffect(() => {
		const { day, month, year } = selectedDay;
		const selectedDateValue = new Date(year, month, day);
		if (!clientId) return;
		dispatch(
			getSessionTypes({
				clientId,
				date: dateNormalizer(dayjs(selectedDateValue)),
				userId,
			}),
		);
	}, [dispatch, clientId, selectedDay, userId]);

	useEffect(() => {
		if (!userId) return;
		const timeout = setTimeout(() => {
			dispatch(
				getUserClients({
					userId,
					page: '1',
					pageSize: pageSize.toString(),
					searchString,
				}),
			)
				.then(unwrapResult)
				.then((response) => {
					if (!response.query) return;
					const clientListValues = response.query.map((client) => ({
						id: client.id as string,
						label: client.fullName,
					}));

					if (!clientListValues.length) {
						dispatch(setClient(null));

						return;
					}
					setClientList(clientListValues);
				});
		}, 1000);

		// TODO - clearTimeout to void
		// eslint-disable-next-line
		return () => clearTimeout(timeout);
	}, [userId, pageSize, searchString, dispatch]);

	useEffect(() => {
		if (startTimeValue) {
			setEndTimeValue(startTimeValue.add(1, 'h'));
		}
	}, [startTimeValue]);

	useEffect(() => {
		const selectedDate = dayjs()
			.set('year', selectedDay.year)
			.set('month', selectedDay.month)
			.set('date', selectedDay.day);
		setDateValue(selectedDate);
	}, [selectedDay]);

	const onClientChange = (value: string) => {
		const client = clientList.find((x) => x.id === value);
		if (!client) return;
		dispatch(setClient(client));
	};

	const onTypeChange = (event: SelectChangeEvent<string>) => {
		const value = event.target.value;
		setSessionTypeId(parseInt(value));
	};

	const addSession = () => {
		if (!startTimeValue || !endTimeValue || !days.length || !dateValue || !clientId) return;
		setLoading(true);

		const hour = startTimeValue.hour();
		const minute = startTimeValue.minute();
		const startTime = dateValue.set('hour', hour).set('minute', minute).format('YYYY-MM-DDTHH:mm');

		const endHour = endTimeValue.hour();
		const endMinute = endTimeValue.minute();
		const endTime = dateValue.set('hour', endHour).set('minute', endMinute).format('YYYY-MM-DDTHH:mm');

		const data: CreateEventProperties = {
			clientId,
			date: dateNormalizer(dateValue),
			startTime,
			endTime,
			sessionTypeId,
		};

		dispatch(createEvent(data))
			.then(unwrapResult)
			.then(() => {
				toast('Created');
				onClose();
			})
			.catch(errorNormalizer)
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		const data = sessionTypesData.filter((sessionType) =>
			sessionType.allowedRoleSections?.find((x) => x.id === role?.section.id),
		);
		setSessionTypes(data);
		if (!isBT && data.length !== 1) return;
		setSessionTypeId(3);
	}, [sessionTypesData, role, isBT]);

	const searchClientHandler = (value: string) => {
		dispatch(setSearchClient(value));
	};

	useEffect(() => {
		if (!sessionTypeId || sessionTypeId !== 9 || !dateValue) return;
		const date = dateNormalizer(dateValue);
		dispatch(getUserRemainingHours({ userId, date, sessionType: sessionTypeId }));
	}, [dispatch, userId, sessionTypeId, dateValue]);

	return (
		<>
			<SearchSelect
				data={clientList}
				label="Client"
				setSearchString={searchClientHandler}
				searchString={searchString}
				setValue={onClientChange}
				hasMore={Boolean(userClients.hasNextPage)}
				setSize={setPageSize}
				startAdornment={<Client />}
				placeholder="Select client"
			/>
			<CustomSelect
				label="Session type"
				data={sessionTypes}
				value={`${sessionTypeId}`}
				setValue={onTypeChange}
				className="marginBottom16"
				startAdornment={<SessionType />}
				disabled={!clientId}
				description={
					userSessionAllocation
						? `${userSessionAllocation.remainingHoursThisMonth} hours remaining out of ${userSessionAllocation.allocatedHoursPerMonth}.`
						: ''
				}
			/>
			<Picker.CustomDate label="Date of session" onChange={setDateValue} value={dateValue} className="marginBottom16" />
			<TimeInputsWrapper>
				<Picker.CustomTime label="Start time" value={startTimeValue} onChange={setStartTimeValue} />
				<Picker.CustomTime
					label="End time"
					value={endTimeValue}
					onChange={setEndTimeValue}
					error={hasError}
					errorMessage={message}
				/>
			</TimeInputsWrapper>
			<CustomButton
				onClick={addSession}
				title="Add calendar event"
				loading={loading}
				disabled={!startTimeValue || !endTimeValue || !clientId || hasError || clientId === '0' || sessionTypeId === 0}
			/>
		</>
	);
};

const AddEvent = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const isAddingSession = useSelector((state) => state.calendar.isCreating);
	const open = useSelector((state) => state.calendarHelper.popups.addScheduled);
	const role = useSelector((state) => state.account.role);
	const isAdmin = role?.section.id === AdminTypes.admin;

	const closeHandler = () => {
		dispatch(setAddScheduledSession(false));
		dispatch(setClient(null));
		dispatch(setTherapist(null));
		dispatch(clearDiscretionarySessionState());
	};

	return (
		<CustomAlert
			isBusy={isAddingSession}
			open={open}
			onClose={closeHandler}
			titleOnTop={false}
			title="Add session"
			Content={() => (isAdmin ? <AddContentAdmin /> : <AddContent onClose={closeHandler} />)}
		/>
	);
};

export default AddEvent;
